import { Image } from 'assets/icons/Image';
import { TreeSelect } from 'antd';
import SimpleInput from 'ui/input';
import SimpleCheckbox from 'ui/checkbox';
import { ObjectEntityFragment } from 'pages/facilities/api/fragments/generated/ObjectEntity.fragment';
import { getImagePath } from 'utils/helpers/get/getImagePath';
import { useImageCrop } from '../../lib/hooks/useImageCrop';
import ReactCrop from 'react-image-crop';
import Modal from 'ui/modal';
import { useState, useEffect } from 'react';
import { useObject } from 'utils/api/useObject';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

const ASPECT_RATIO = 1;

interface TreeDataNode {
  title: string;
  value: string;
  children?: TreeDataNode[];
}

interface BasicSectionProps {
  values: any;
  touched: any;
  errors: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  onParentObjectChange: (value: string) => void;
  parentObjects: ObjectEntityFragment[];
  onParentObjectSearch: (value: string) => void;
  onParentObjectScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const BasicSection = ({
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  onParentObjectChange,
  parentObjects,
  onParentObjectSearch,
  onParentObjectScroll,
}: BasicSectionProps) => {
  const { object } = useObject();
  const { facilityId } = useParams();
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  const {
    previewUrl,
    crop,
    setCrop,
    setCompletedCrop,
    isModalOpen,
    tempImageUrl,
    imgRef,
    handleFileChange,
    onImageLoad,
    handleCropComplete,
    handleModalCancel,
  } = useImageCrop({
    onCropComplete: croppedImage => {
      setFieldValue('avatar', croppedImage);
    },
  });

  const transformParentObjectsToTreeData = (objects: ObjectEntityFragment[]): TreeDataNode[] => {
    return objects.map(object => ({
      title: object.name,
      value: object.id,
      children: object.children ? transformParentObjectsToTreeData(object.children) : undefined,
    }));
  };

  const parentObjectsTreeData = transformParentObjectsToTreeData(parentObjects);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
      }
    };
  }, [previewUrl, tempImageUrl]);

  useEffect(() => {
    if (object?.settings?.avatar?.path) {
      setAvatarPreview(getImagePath(object.settings.avatar.path));
    }
  }, [object]);

  const handleParentObjectChange = (value: string) => {
    onParentObjectChange(value);
  };

  return (
    <>
      <Modal
        title="Редактировать аватар"
        open={isModalOpen}
        onOk={handleCropComplete}
        onCancel={handleModalCancel}
        width={520}
        maskClosable={false}
        footer={[
          <button key="save" className="btn-primary" onClick={handleCropComplete}>
            Сохранить
          </button>,
        ]}
      >
        <div className="mt-4">
          <ReactCrop
            crop={crop}
            aspect={ASPECT_RATIO}
            onChange={c => {
              const size = Math.min(c.width, c.height);
              setCrop({
                ...c,
                width: size,
                height: size,
              });
            }}
            onComplete={c => setCompletedCrop(c)}
            className="w-full"
          >
            <img
              ref={imgRef}
              src={tempImageUrl || ''}
              alt="Crop"
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </div>
      </Modal>

      <div className="col-span-full">
        <span className="inline-block Subtitle1 text-smena_text-secondary mb-4">Основная информация</span>

        <div className="flex gap-8">
          <div>
            <span className="Subtitle2">Аватар</span>
            <label
              htmlFor="file-upload"
              className={clsx(
                'w-[156px] h-[156px] flex flex-col items-center justify-center rounded-lg cursor-pointer hover:bg-gray-50 overflow-hidden relative',
                !previewUrl && !avatarPreview ? 'border-2 border-dashed border-[#B3CDFF]' : '',
              )}
            >
              {previewUrl || avatarPreview ? (
                <>
                  <img
                    src={previewUrl || avatarPreview}
                    alt="Preview"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-sm">Изменить</div>
                  </div>
                </>
              ) : (
                <div className="text-center flex flex-col items-center justify-center">
                  <Image />
                  <div className="mt-3 text-sm leading-6 text-gray-600">
                    <div className="Body2 text-primary cursor-pointer w-[70px]">Загрузить Аватар</div>
                  </div>
                </div>
              )}
              <input
                id="file-upload"
                name="avatar"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
            {touched.avatar && errors.avatar && <p className="mt-1 text-sm text-red-600">{errors.avatar}</p>}
          </div>

          <div className="flex-1 grid grid-cols-6 gap-5">
            <div className="col-span-full">
              <SimpleInput
                divClassName="w-full"
                label="Название объекта"
                placeholder="Введите название объекта"
                onChange={handleChange}
                value={values.name || ''}
                type="text"
                name="name"
                error={touched.name && errors.name ? errors.name : ''}
              />
            </div>

            <div className="col-span-full">
              <SimpleCheckbox
                label="Разрешить наследоваться от этого объекта"
                checked={values.allowInheritance || false}
                onChange={e => setFieldValue('allowInheritance', e.target.checked)}
                name="allowInheritance"
                disabled={!!object}
              />
            </div>

            <div className="col-span-full">
              <label className="label-primary">Родительский объект (если нужно)</label>
              <TreeSelect
                className="w-full"
                showSearch
                placeholder="Выберите родительский объект"
                onChange={handleParentObjectChange}
                value={!facilityId ? values.parentFacilityId : object?.parent?.name}
                treeData={parentObjectsTreeData}
                treeDefaultExpandAll
                allowClear
                onSearch={onParentObjectSearch}
                onPopupScroll={onParentObjectScroll}
                listHeight={200}
                disabled={!!object}
              />
              {touched.parentFacilityId && errors.parentFacilityId && (
                <div className="error-message">{errors.parentFacilityId}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BasicSection;
